import { istCreatorKonto, t, ta } from "../../../lib/sprache";
import { MODUL_AB_TARIF, modulGesperrt, tarifKeyMitModul, tarifMitModul, wechselLink, type ModulKey, type TarifKatalog } from "../../../model/tarif";

/**
 * IA-1 (Inhaber 21.10.2026, Zoho-Vorbild „Alle Einstellungen“): der Einstellungsbaum als reine Daten. Gruppen und Karten,
 * Ziel jeder Karte (eigene Seite oder Anker in einer Seite), was die Suche findet (Kartentitel, Abschnittsüberschriften,
 * Feldbeschriftungen, Synonyme), wer welche Karte sieht und die Weiterleitungen alter Wege. Keine Datenzugriffe, damit
 * Suche, Rechte und Weiterleitungen ohne Browser testbar sind.
 *
 * Anker sind stabil: Mails (link_path), Hilfeartikel und Coco verweisen auf sie. Wer einen Anker umbenennt, lässt den alten
 * Namen als Weiterleitung stehen (einstellungen-wege.test.ts prüft alle Verweise im Code und in den Migrationen).
 */

export const EINST = "/agentur/einstellungen";
export const PROFIL_WEG = "/agentur/profil";
export const SICHERHEIT_WEG = `${EINST}/sicherheit`;

/** Seiten mit eigener Adresse unter /agentur/einstellungen/… (Agentur-Themen, Vollbild „Alle Einstellungen“) */
export const SEITEN = [
  "firma", "branding", "region", "team", "rollen", "recht",
  "provision", "abrechnungsweg", "zahlungsziele", "pruefregeln", "wiedervorlagen", "staffel", "herkunft-nachprovision", "gutschriften", "mahnleiter",
  "nummernkreise", "positionsarten", "plattformen", "verguetung", "pdf-vorlagen", "vorlagen",
  "kooperationen", "marken-freigabe", "anfragen", "creator-sicht", "module",
  "mailversand", "export", "whatsapp",
  "abo",
] as const;
export type SeiteKey = (typeof SEITEN)[number];
export function istSeite(v: string | undefined): v is SeiteKey {
  return (SEITEN as readonly string[]).includes(v ?? "");
}

/** Persönliche Seiten: stehen im Profilmenü oben rechts und bleiben im Arbeitsrahmen (kein Vollbild) */
export type PersoenlichKey = "profil" | "sicherheit";

export type GruppeKey = "agentur" | "team" | "steuern" | "geld" | "anpassung" | "module" | "verbindungen" | "konto" | "persoenlich";

export interface Gruppe {
  key: GruppeKey;
  titel: () => string;
}
export const GRUPPEN: readonly Gruppe[] = [
  // Studio (Creator-Konto): kein „Agentur“ für das eigene Konto
  { key: "agentur", titel: () => ta("Agentur", "Agency", "Profil & Auftritt", "Profile & branding") },
  { key: "team", titel: () => t("Team & Rollen", "Team & roles") },
  { key: "steuern", titel: () => t("Steuern & Recht", "Tax & legal") },
  { key: "geld", titel: () => t("Geld & Abrechnung", "Money & billing") },
  { key: "anpassung", titel: () => t("Anpassung", "Customisation") },
  { key: "module", titel: () => t("Module", "Modules") },
  { key: "verbindungen", titel: () => t("Verbindungen", "Connections") },
  { key: "konto", titel: () => t("Konto", "Account") },
  { key: "persoenlich", titel: () => t("Persönlich", "Personal") },
];
export function gruppeTitel(key: GruppeKey): string {
  return GRUPPEN.find((g) => g.key === key)?.titel() ?? key;
}

export interface Karte {
  key: string;
  gruppe: GruppeKey;
  /** Seite in „Alle Einstellungen“ (für die Markierung im Baum); null: Ziel außerhalb (Profil, Instagram, Glocke) */
  seite: SeiteKey | PersoenlichKey | null;
  /** Anker in der Seite (ohne #) */
  anker?: string;
  /** Ziel mit Anker */
  to: string;
  titel: () => string;
  /** Abschnittsüberschriften und Feldbeschriftungen der Seite (Sprache der Oberfläche) */
  begriffe: () => string[];
  /** Synonyme (Sprache der Oberfläche) */
  synonyme?: () => string[];
  /** Begriffe ohne Sprache (IBAN, DATEV, 2FA …) */
  neutral?: readonly string[];
  /** Im Creator-Konto (Studio) sichtbar */
  studio: boolean;
  /** Tarifmodul, das die Seite braucht (I-1): ohne Modul zeigt die Karte „ab <Tarif>“ und führt zum Upgrade statt zur Seite */
  modul?: ModulKey;
  /** Verständlichkeit A2-01: ein Satz zum Zweck der Seite (statt der ersten Feldnamen) */
  kurz?: () => string;
  /** Verständlichkeit A2-02: für den Start nicht nötig, der Standard passt für die meisten */
  optional?: boolean;
}

/**
 * Begriffe und Synonyme einer Karte: „|“ trennt, ein vorangestelltes „!“ markiert ein Feld, das es im Studio (Creator-Konto)
 * nicht gibt. Die Seiten blenden genau diese Felder mit derselben Bedingung aus (istCreatorKonto()), damit die Suche im Studio
 * nichts findet, was die Seite nicht zeigt (Studio-Texte F3).
 */
export function liste(text: string): string[] {
  const studio = istCreatorKonto();
  return text.split("|").filter((x) => !(studio && x.startsWith("!"))).map((x) => x.replace(/^!/, ""));
}

const w = (seite: SeiteKey, anker?: string) => `${EINST}/${seite}${anker ? `#${anker}` : ""}`;

/** Alle Karten in der Reihenfolge der Startseite. */
export const KARTEN: readonly Karte[] = [
  // ── Agentur ──
  {
    key: "firma", gruppe: "agentur", seite: "firma", to: w("firma"), studio: true,
    titel: () => t("Profil & Rechtsangaben", "Profile & legal details"),
    kurz: () => t("Name, Anschrift und Rechtsangaben für Verträge, Rechnungen und Impressum.", "Name, address and legal details for contracts, invoices and legal notice."),
    begriffe: () => liste(t("Firmenname|!Agenturname im Portal|Adresse|Rechtsangaben|Rechtsform|Vertreten durch|Registernummer|Registergericht|Telefon|Website|Kontakt für Datenschutz|Anwendbares Recht|Gerichtsstand|Zusatzangaben fürs Impressum", "Company name|!Agency name in the portal|Address|Legal details|Legal form|Represented by|Register number|Register court|Phone|Website|Privacy contact|Governing law|Place of jurisdiction|Additional legal notice details")),
    synonyme: () => liste(t("Firma|Firmendaten|Anschrift|Handelsregister|Geschäftsführer|Impressum|Vertretung", "Company|Company details|Postal address|Commercial register|Managing director|Legal notice|Representation")),
  },
  {
    key: "bank", gruppe: "agentur", seite: "firma", anker: "bank", to: w("firma", "bank"), studio: true,
    titel: () => t("Bankverbindungen", "Bank details"),
    kurz: () => t("Konto, auf das Marken zahlen; steht auf jeder Rechnung.", "Account brands pay into; shown on every invoice."),
    begriffe: () => liste(t("Bankverbindungen|Kontoinhaber|Standardkonto|QR-Rechnung", "Bank details|Account holder|Default account|QR bill")),
    synonyme: () => liste(t("Bank|Konto|Bankkonto|Kontonummer", "Bank|Account|Bank account|Account number")),
    neutral: ["IBAN", "BIC", "SWIFT", "QR"],
  },
  {
    key: "branding", gruppe: "agentur", seite: "branding", to: w("branding"), studio: true,
    titel: () => t("Branding", "Branding"),
    kurz: () => t("Logo und Farbe für Portal, PDFs und Mails (optional).", "Logo and colour for portal, PDFs and emails (optional)."),
    optional: true,
    begriffe: () => liste(t("!Agenturlogo|Akzentfarbe|Eigener Portalname", "!Agency logo|Accent colour|Custom portal name")),
    synonyme: () => liste(t("Logo|Farbe|Markenfarbe|Aussehen|Portalname", "Logo|Colour|Brand colour|Look|Portal name")),
  },
  {
    key: "region", gruppe: "agentur", seite: "region", to: w("region"), studio: true,
    titel: () => t("Sprache & Region", "Language & region"),
    kurz: () => t("Sprache, Zahlenformat und Zeitzone; vorbelegt aus dem Land.", "Language, number format and time zone; prefilled from the country."),
    optional: true,
    begriffe: () => liste(t("Sprache der Belege|Sprachen für Vorlagen und Texte|Hauswährung|Weitere Rechnungswährungen|Zahlen- und Datumsformat|Land|Zeitzone", "Document language|Languages for templates and texts|Home currency|Additional invoice currencies|Number and date format|Country|Time zone")),
    synonyme: () => liste(t("Sprache|Währung|Datumsformat|Zahlenformat|Zeitzone|Englisch", "Language|Currency|Date format|Number format|Time zone|German")),
    neutral: ["EUR", "CHF"],
  },
  // ── Team & Rollen ──
  {
    key: "team", gruppe: "team", seite: "team", to: w("team"), studio: false,
    titel: () => t("Mitglieder und Rechte", "Members and permissions"),
    kurz: () => t("Manager einladen und Zugänge verwalten.", "Invite managers and manage access."),
    optional: true,
    begriffe: () => liste(t("Manager einladen|Creator-Zuweisungen|Namen und Funktionen|Agentur übergeben|Creator-Aktivität|Letzte Änderungen am Team|Sichtbereich|Finanzrechte", "Invite manager|Creator assignments|Names and job titles|Hand over agency|Creator activity|Recent team changes|Visible scope|Financial permissions")),
    synonyme: () => liste(t("Team|Mitarbeiter|Benutzer|Einladen|Zuweisung", "Team|Staff|Users|Invite|Assignment")),
  },
  {
    key: "rollen", gruppe: "team", seite: "rollen", to: w("rollen"), studio: false,
    titel: () => t("Rollen & Rechte", "Roles & permissions"),
    kurz: () => t("Wer was sehen und tun darf (optional).", "Who may see and do what (optional)."),
    optional: true,
    begriffe: () => liste(t("Rollen|Rechte je Person|Rechte-Vorlagen|Assistenz|Talent-Management|Buchhaltung|Sichtbereich", "Roles|Permissions per person|Permission templates|Assistant|Talent management|Accounting|Visible scope")),
    synonyme: () => liste(t("Rolle|Rechte|Berechtigung|Admin|Manager|Zugriff", "Role|Permissions|Authorisation|Admin|Manager|Access")),
  },
  {
    key: "team-sicherheit", gruppe: "team", seite: "team", anker: "sicherheit", to: w("team", "sicherheit"), studio: false,
    titel: () => t("Sicherheit fürs Team", "Team security"),
    kurz: () => t("Zwei-Faktor-Pflicht und Anmelderegeln fürs Team (optional).", "Two-factor requirement and sign-in rules for the team (optional)."),
    optional: true,
    begriffe: () => liste(t("Zweiter Faktor fürs Team|Support-Zugänge", "Second factor for the team|Support access")),
    synonyme: () => liste(t("Sicherheit|Zwei-Faktor|Zweiter Faktor|Authenticator|Support", "Security|Two-factor|Second factor|Authenticator|Support")),
    neutral: ["2FA", "MFA"],
  },
  // ── Steuern & Recht ──
  {
    key: "steuer", gruppe: "steuern", seite: "firma", anker: "steuer", to: w("firma", "steuer"), studio: true,
    titel: () => t("Steuerstatus", "Tax status"),
    kurz: () => t("Steuerstatus und Steuernummern; davon hängt die Umsatzsteuer auf Rechnungen ab.", "Tax status and tax numbers; the VAT on invoices depends on them."),
    begriffe: () => liste(t("Steuerstatus|Regelbesteuerung|Kleinunternehmer|Steuernummer|Umsatzsteuer-Identifikationsnummer", "Tax status|Standard taxation|Small business|Tax number|VAT identification number")),
    synonyme: () => liste(t("Steuer|Umsatzsteuer|Mehrwertsteuer|Kleinunternehmerregelung", "Tax|Sales tax|Value added tax|Small business scheme")),
    neutral: ["USt", "USt-IdNr", "UID", "MWST", "VAT"],
  },
  {
    key: "recht", gruppe: "steuern", seite: "recht", to: w("recht"), studio: true,
    titel: () => t("Verträge & Rechtstexte", "Contracts & legal texts"),
    kurz: () => t("Deine AGB und Vertragsbedingungen (optional).", "Your terms and contract conditions (optional)."),
    optional: true,
    begriffe: () => liste(t("Verträge mit CreatorOffice|Aktuelle Fassungen|Zustimmungen|Frühere Fassungen|Vertragspartner", "Contracts with CreatorOffice|Current versions|Consents|Earlier versions|Contractual partner")),
    synonyme: () => liste(t("Rechtstexte|Datenschutz|Auftragsverarbeitung|Nachweis|Geschäftsbedingungen", "Legal texts|Privacy|Data processing|Proof|Terms and conditions")),
    neutral: ["AGB", "AVV", "DPA"],
  },
  // ── Geld & Abrechnung ──
  {
    key: "provision", gruppe: "geld", seite: "provision", to: w("provision"), studio: false,
    titel: () => t("Provision", "Commission"),
    kurz: () => t("Dein Prozentsatz vom Creator-Honorar und die Agenturgebühr an die Marke.", "Your percentage of the creator fee and the agency fee to the brand."),
    begriffe: () => liste(t("Standardprovision in Prozent|Art der Agenturgebühr|Agenturgebühr|Provisionsbasis", "Default commission in percent|Type of agency fee|Agency fee|Commission basis")),
    synonyme: () => liste(t("Provision|Kommission|Gebühr|Satz|Agenturanteil", "Commission|Fee|Rate|Agency share")),
  },
  {
    key: "abrechnungsweg", gruppe: "geld", seite: "abrechnungsweg", to: w("abrechnungsweg"), studio: false,
    titel: () => t("Abrechnungsweg", "Invoicing route"),
    kurz: () => t("Wer der Marke die Rechnung stellt: du oder der Creator.", "Who invoices the brand: you or the creator."),
    begriffe: () => liste(t("Abrechnungsweg|Auszahlung an Creator", "Invoicing route|Payout to creators")),
    synonyme: () => liste(t("Abrechnung|Auszahlung|Vollbetrag|Provisionsrechnung", "Billing|Payout|Full amount|Commission invoice")),
  },
  {
    key: "zahlungsziele", gruppe: "geld", seite: "zahlungsziele", to: w("zahlungsziele"), studio: true,
    titel: () => t("Zahlungsziele", "Payment terms"),
    kurz: () => t("In wie vielen Tagen deine Rechnungen zu zahlen sind.", "Within how many days your invoices must be paid."),
    begriffe: () => liste(t("Zahlungsziel in Tagen|Zahlungsziel für Marken in Tagen|!Zahlungsziel im Rechnungsgenerator der Creator|Fenster bald fällig|!Wartezeit auf die Antwort des Creators|Laufzeit der Signierlinks|!Gültigkeit von Einladungen", "Payment term in days|Payment term for brands in days|!Payment term in the creators' invoice generator|Due soon window|!Waiting time for the creator's reply|Validity of signing links|!Validity of invitations")),
    synonyme: () => liste(t("Zahlungsziel|Fälligkeit|Frist|Fristen|Signierlink|!Einladung", "Payment term|Due date|Deadline|Deadlines|Signing link|!Invitation")),
  },
  {
    key: "pruefregeln", gruppe: "geld", seite: "pruefregeln", to: w("pruefregeln"), studio: true,
    titel: () => t("Prüfregeln", "Check rules"),
    kurz: () => t("Hinweise, bevor eine Rechnung endgültig erstellt wird (optional).", "Notices before an invoice is issued (optional)."),
    optional: true,
    begriffe: () => liste(t("Prüfregeln für Rechnungen|Abweichung vom Kooperationswert|Mindestlänge der Leistungsbeschreibung|Rechnungsdatum nie auf Wochenende oder Feiertag|Kleinbetragsrechnung|!Stichworte für provisionsfreie Auslagen", "Invoice check rules|Deviation from the deal value|Minimum length of the service description|Invoice date never on a weekend or public holiday|Small-amount invoice|!Keywords for commission-free expenses")),
    synonyme: () => liste(t("Prüfung|Plausibilität|!Auslagen|Kontrolle", "Check|Plausibility|!Expenses|Control")),
  },
  {
    key: "wiedervorlagen", gruppe: "geld", seite: "wiedervorlagen", to: w("wiedervorlagen"), studio: true,
    titel: () => t("Wiedervorlagen", "Follow-ups"),
    kurz: () => t("Wann das Portal an offene Punkte erinnert (optional).", "When the portal reminds you of open items (optional)."),
    optional: true,
    begriffe: () => liste(t("!Fehlende Creator-Rechnung ist dringend ab Tag|Entwurf gilt als liegengeblieben ab Tag|Feiertagsregion", "!Missing creator invoice is urgent from day|Draft counts as stalled from day|Public holiday region")),
    synonyme: () => liste(t("Wiedervorlage|Feiertag|Feiertage|Werktage|Liegengeblieben|Dringend", "Follow-up|Public holiday|Holidays|Working days|Stalled|Urgent")),
  },
  {
    key: "staffel", gruppe: "geld", seite: "staffel", to: w("staffel"), studio: false,
    titel: () => t("Gestaffelte Provision", "Tiered commission"),
    kurz: () => t("Weniger Provision, wenn ein Creator mehr verdient (optional).", "Less commission when a creator earns more (optional)."),
    optional: true,
    begriffe: () => liste(t("Gestaffelte Provision je Creator", "Tiered commission per creator")),
    synonyme: () => liste(t("Staffel|Stufen|Umsatzstaffel", "Tiers|Levels|Revenue tiers")),
  },
  {
    key: "herkunft", gruppe: "geld", seite: "herkunft-nachprovision", to: w("herkunft-nachprovision"), studio: false,
    titel: () => t("Herkunft & Nachprovision", "Origin & post-term commission"),
    kurz: () => t("Andere Provision je nach Herkunft eines Deals und nach dem Ausstieg eines Creators (optional).", "Different commission by deal origin and after a creator leaves (optional)."),
    optional: true,
    begriffe: () => liste(t("Herkunft und Nachprovision", "Origin and post-term commission")),
    synonyme: () => liste(t("Nachprovision|Nachlauf|Herkunft|Vermittlung|Vertragsende", "Post-term commission|Tail|Origin|Referral|End of contract")),
  },
  {
    key: "gutschriften", gruppe: "geld", seite: "gutschriften", to: w("gutschriften"), studio: false, modul: "module_payouts",
    titel: () => t("Gutschriften", "Self-billing"),
    kurz: () => t("Wenn du die Rechnung für den Creator schreibst (optional).", "When you write the invoice for the creator (optional)."),
    optional: true,
    begriffe: () => liste(t("E-Rechnung zur Gutschrift|Gutschriftverfahren", "E-invoice with self-billing invoices|Self-billing procedure")),
    synonyme: () => liste(t("Gutschrift|E-Rechnung", "Self-billing invoice|E-invoice")),
    neutral: ["XRechnung"],
  },
  {
    key: "mahnwesen", gruppe: "geld", seite: "mahnleiter", to: w("mahnleiter"), studio: true,
    titel: () => t("Mahnwesen", "Dunning"),
    kurz: () => t("Wann und wie oft säumige Marken erinnert und gemahnt werden.", "When and how often late-paying brands are reminded."),
    begriffe: () => liste(t("Mahnleiter|Mahnstufen|Zahlungserinnerung|Mahngebühr|Verzugszinsen|Basiszins|Mahnsperre", "Dunning levels|Dunning steps|Payment reminder|Dunning fee|Default interest|Base rate|Dunning block")),
    synonyme: () => liste(t("Mahnung|Mahngebühren|Mahnpauschale|Inkasso|Erinnerung", "Dunning|Dunning fees|Flat dunning fee|Debt collection|Reminder")),
  },
  // ── Anpassung ──
  {
    key: "nummernkreise", gruppe: "anpassung", seite: "nummernkreise", to: w("nummernkreise"), studio: true,
    titel: () => t("Nummernkreise", "Number ranges"),
    kurz: () => t("Aufbau der Rechnungs-, Angebots- und Vertragsnummern.", "Format of invoice, offer and contract numbers."),
    begriffe: () => liste(t("Präfix der Rechnungsnummer|Nummernmuster|Nächste laufende Nummer|!Muster der Gutschriftnummern|Angebotsnummer|Rückfrage bei rückdatierten Rechnungen", "Invoice number prefix|Number pattern|Next sequential number|!Self-billing invoice number pattern|Offer number|Question for backdated invoices")),
    synonyme: () => liste(t("Nummer|Rechnungsnummer|Nummernkreis|Belegnummer|!Gutschriftnummer|Präfix", "Number|Invoice number|Number range|Document number|!Self-billing number|Prefix")),
  },
  {
    key: "positionsarten", gruppe: "anpassung", seite: "positionsarten", to: w("positionsarten"), studio: true,
    titel: () => t("Positionsarten & Artikel", "Item types & articles"),
    kurz: () => t("Vorlagen für Rechnungspositionen und Artikel (optional).", "Templates for invoice items and articles (optional)."),
    optional: true,
    begriffe: () => liste(t("Positionsarten|Artikelkatalog|!Provisionspflichtig", "Item types|Item catalogue|!Commissionable")),
    synonyme: () => liste(t("Artikel|Produkte|Leistungen|Zusatzpositionen|Ausfallhonorar|Bonus", "Articles|Products|Services|Extra items|Cancellation fee|Bonus")),
  },
  {
    key: "plattformen", gruppe: "anpassung", seite: "plattformen", to: w("plattformen"), studio: true,
    titel: () => t("Plattformen", "Platforms"),
    kurz: () => t("Welche Plattformen in Kooperationen zur Auswahl stehen (optional).", "Which platforms can be chosen in deals (optional)."),
    optional: true,
    begriffe: () => liste(t("Plattformen im Kooperationsformular", "Platforms in the deal form")),
    synonyme: () => liste(t("Plattform|Kanal|Kanäle", "Platform|Channel|Channels")),
    neutral: ["TikTok", "YouTube"],
  },
  {
    key: "verguetung", gruppe: "anpassung", seite: "verguetung", to: w("verguetung"), studio: true,
    titel: () => t("Vergütungsmodelle", "Remuneration models"),
    kurz: () => t("Vergütungsmodelle für Kooperationen (optional).", "Remuneration models for deals (optional)."),
    optional: true,
    begriffe: () => liste(t("Zugelassene Vergütungsmodelle|!Pflichtangaben im Creator-Profil vor der ersten Rechnung|Pauschalversteuerung", "Permitted remuneration models|!Required details in the creator profile before the first invoice|Flat-rate taxation")),
    synonyme: () => liste(t("Vergütung|Modell|Festpreis|Pauschal", "Remuneration|Model|Fixed price|Flat rate")),
  },
  {
    key: "pdf-vorlagen", gruppe: "anpassung", seite: "pdf-vorlagen", to: w("pdf-vorlagen"), studio: true,
    titel: () => t("PDF-Vorlagen", "PDF templates"),
    kurz: () => t("Aussehen von Rechnungen, Angeboten und Verträgen als PDF (optional).", "Look of invoices, offers and contracts as PDF (optional)."),
    optional: true,
    begriffe: () => liste(t("Belege als PDF|Rechnung|!Gutschrift|Mahnschreiben|Angebot|Vertragsvorlagen", "Documents as PDF|Invoice|!Self-billing invoice|Dunning letter|Offer|Contract templates")),
    synonyme: () => liste(t("PDF|Vorlage|Vertragsvorlage|Briefpapier|Layout|Druck", "PDF|Template|Contract template|Letterhead|Layout|Print")),
  },
  {
    key: "vorlagen", gruppe: "anpassung", seite: "vorlagen", to: w("vorlagen"), studio: true,
    titel: () => t("Mailvorlagen", "Email templates"),
    kurz: () => t("Texte der Mails an Marken und Creator (optional).", "Texts of emails to brands and creators (optional)."),
    optional: true,
    begriffe: () => liste(t("Text für Mails, Mahnschreiben und Rechnungshinweise|Vertragsvorlagen|Status und Begriffe|Zwischenstände", "Text for emails, dunning letters and invoice notes|Contract templates|Statuses and terms|Interim statuses")),
    synonyme: () => liste(t("Vorlagen|Mailtexte|Mahntexte|Texte|Mail|Platzhalter", "Templates|Email texts|Dunning texts|Texts|Email|Placeholders")),
  },
  // ── Module ──
  {
    key: "kooperationen", gruppe: "module", seite: "kooperationen", to: w("kooperationen"), studio: true,
    titel: () => t("Kooperationen", "Deals"),
    kurz: () => t("Ab wann eine Kooperation als Umsatz zählt und weitere Vorgaben (optional).", "From when a deal counts as revenue and other defaults (optional)."),
    optional: true,
    begriffe: () => liste(t("Enthaltene Korrekturschleifen|Umsatzbeginn|Vergütung je weiterer Schleife", "Included revision rounds|Revenue start|Fee per additional round")),
    synonyme: () => liste(t("Kooperation|Korrektur|Korrekturschleifen|Deals", "Deal|Revision|Revision rounds|Collaboration")),
    // Studio-Texte F4: im Studio heißen Kooperationen „Deals“ (t() übersetzt auch die Synonyme); das alte Wort findet die Seite trotzdem
    neutral: ["Kooperation", "Kooperationen", "Collaboration", "Collaborations"],
  },
  {
    key: "marken-freigabe", gruppe: "module", seite: "marken-freigabe", to: w("marken-freigabe"), studio: true, modul: "module_brand_review",
    titel: () => t("Marken-Freigabe", "Brand approval"),
    kurz: () => t("Wie Marken Inhalte freigeben (optional).", "How brands approve content (optional)."),
    optional: true,
    begriffe: () => liste(t("Freigabe durch die Marke und Bericht", "Approval by the brand and report")),
    synonyme: () => liste(t("Freigabe|Bericht|Freigabelink|Content prüfen", "Approval|Report|Approval link|Review content")),
  },
  {
    key: "anfragen", gruppe: "module", seite: "anfragen", to: w("anfragen"), studio: true, modul: "module_pipeline",
    titel: () => t("Anfragen & Angebote", "Inquiries & offers"),
    kurz: () => t("Vorgaben für Anfragen und Angebote an Marken (optional).", "Defaults for inquiries and offers to brands (optional)."),
    optional: true,
    begriffe: () => liste(t("Gültigkeit eines Angebots in Tagen|Nachfassen nach Tagen ohne Antwort|Preise im Angebot|Absagegründe|Standard-Einleitung im Angebot|Standard-Bedingungen im Angebot", "Offer validity in days|Follow up after days without reply|Prices in the offer|Decline reasons|Default introduction in the offer|Default terms in the offer")),
    synonyme: () => liste(t("Angebot|Anfrage|Pipeline|Absage", "Offer|Inquiry|Pipeline|Decline")),
  },
  {
    key: "creator-sicht", gruppe: "module", seite: "creator-sicht", to: w("creator-sicht"), studio: false,
    titel: () => t("Creator-Sicht", "Creator view"),
    kurz: () => t("Was Creator im Portal sehen (optional).", "What creators see in the portal (optional)."),
    optional: true,
    begriffe: () => liste(t("Was Creator im Portal sehen", "What creators see in the portal")),
    synonyme: () => liste(t("Creator-Portal|Sichtbarkeit|Beträge für Creator", "Creator portal|Visibility|Amounts for creators")),
  },
  {
    key: "module", gruppe: "module", seite: "module", to: w("module"), studio: false,
    titel: () => t("Was ist in meinem Tarif an", "What my plan includes"),
    kurz: () => t("Welche Bereiche dein Tarif enthält.", "Which areas your plan includes."),
    optional: true,
    begriffe: () => liste(t("Module im Tarif", "Modules in the plan")),
    synonyme: () => liste(t("Module|Funktionen|Freigeschaltet|Enthalten", "Modules|Features|Enabled|Included")),
  },
  // ── Verbindungen ──
  {
    key: "mailversand", gruppe: "verbindungen", seite: "mailversand", to: w("mailversand"), studio: true,
    titel: () => t("Mailversand & Absenderdomain", "Email sending & sender domain"),
    kurz: () => t("Absendername, Antwortadresse und eigene Absenderdomain.", "Sender name, reply address and own sender domain."),
    begriffe: () => liste(t("Absendername|Antwortadresse|Fußzeile für Mails und Mahnschreiben|Absender und Antworten|Signatur|Eigene Absenderdomain|Blindkopie", "Sender name|Reply address|Footer for emails and dunning letters|Sender and replies|Signature|Custom sender domain|Blind copy")),
    synonyme: () => liste(t("Absender|Absenderadresse|Domain|Antworten|Mailversand", "Sender|Sender address|Domain|Replies|Email sending")),
    neutral: ["DNS", "DKIM", "SPF", "Reply-To"],
  },
  {
    key: "export", gruppe: "verbindungen", seite: "export", to: w("export"), studio: true,
    titel: () => t("Buchhaltung & Export", "Accounting & export"),
    kurz: () => t("Rechnungen an deine Buchhaltung oder Steuerkanzlei geben (optional).", "Pass invoices to your accounting or tax office (optional)."),
    optional: true,
    begriffe: () => liste(t("Export|Buchungsstapel|Datenexport", "Export|Posting batch|Data export")),
    synonyme: () => liste(t("Buchhaltung|Steuerberater|Kanzlei|Buchungen", "Accounting|Tax adviser|Accounting firm|Bookings")),
    neutral: ["DATEV", "BMD", "CSV", "ZIP"],
  },
  {
    key: "whatsapp", gruppe: "verbindungen", seite: "whatsapp", to: w("whatsapp"), studio: false,
    titel: () => t("WhatsApp", "WhatsApp"),
    kurz: () => t("Nachrichten an Creator über WhatsApp (optional).", "Messages to creators via WhatsApp (optional)."),
    optional: true,
    begriffe: () => liste(t("WhatsApp-Hinweise|Ereignisse", "WhatsApp notices|Events")),
    synonyme: () => liste(t("Nachrichten an Creator|Handy", "Messages to creators|Phone")),
    neutral: ["WhatsApp"],
  },
  {
    key: "instagram", gruppe: "verbindungen", seite: null, to: "/agentur/creator/insights", studio: false,
    titel: () => t("Instagram", "Instagram"),
    kurz: () => t("Instagram-Konten der Creator verbinden (optional).", "Connect creators' Instagram accounts (optional)."),
    optional: true,
    begriffe: () => liste(t("Instagram-Konten|Insights|Freigaben an Marken", "Instagram accounts|Insights|Shares with brands")),
    synonyme: () => liste(t("Kennzahlen|Reichweite", "Metrics|Reach")),
    neutral: ["Instagram", "Meta"],
  },
  // ── Konto ──
  {
    key: "abo", gruppe: "konto", seite: "abo", to: w("abo"), studio: true,
    titel: () => t("Abo & Tarif", "Subscription & plan"),
    kurz: () => t("Tarif, Testphase und Abrechnung von CreatorOffice.", "Plan, trial and billing of CreatorOffice."),
    optional: true,
    begriffe: () => liste(t("Tarif|Testphase|!Zusätzliche Plätze und Creator|Tarife vergleichen|Module", "Plan|Trial|!Additional seats and creators|Compare plans|Modules")),
    synonyme: () => liste(t("Tarif|Abo|Abonnement|Upgrade|Preis|Paket", "Plan|Subscription|Upgrade|Price|Package")),
  },
  {
    key: "zahlungsart", gruppe: "konto", seite: "abo", anker: "zahlungsart", to: w("abo", "zahlungsart"), studio: true,
    titel: () => t("Zahlungsart & Rechnungen", "Payment method & invoices"),
    kurz: () => t("Zahlungsart und Rechnungen von CreatorOffice.", "Payment method and invoices from CreatorOffice."),
    optional: true,
    begriffe: () => liste(t("Rechnungen und Zahlungsart verwalten|Zahlungsart ändern oder entfernen", "Manage invoices and payment method|Change or remove payment method")),
    synonyme: () => liste(t("Zahlungsart|Zahlungsmethode|Kreditkarte|Lastschrift|Rechnungsadresse|Auf Rechnung|Überweisung|Bankverbindung", "Payment method|Credit card|Direct debit|Billing address|Pay by invoice|Bank transfer|Bank details")),
    neutral: ["Stripe", "PayPal", "SEPA"],
  },
  {
    key: "kuendigung", gruppe: "konto", seite: "abo", anker: "kuendigung-title", to: w("abo", "kuendigung-title"), studio: true,
    titel: () => t("Kündigung & Daten", "Termination & data"),
    kurz: () => t("Abo beenden und Daten exportieren oder löschen.", "End the subscription and export or delete data."),
    optional: true,
    begriffe: () => liste(t("Kündigung und Daten|Abo kündigen|Abo pausieren|Konto löschen|Test beenden", "Termination and data|Cancel subscription|Pause subscription|Delete account|End trial")),
    synonyme: () => liste(t("Kündigen|Löschen|Beenden|Pausieren|Pause|Kündigung zurücknehmen", "Cancel|Delete|Terminate|Pause|Withdraw cancellation")),
  },
  {
    key: "aufbewahrung", gruppe: "konto", seite: "abo", anker: "aufbewahrung", to: w("abo", "aufbewahrung"), studio: true,
    titel: () => t("Aufbewahrung", "Retention"),
    kurz: () => t("Wie lange Belege aufbewahrt werden.", "How long documents are kept."),
    optional: true,
    begriffe: () => liste(t("Aufbewahrung|Nur lesen|Export-Phase|Sperre", "Retention|Read-only|Export phase|Locked")),
    synonyme: () => liste(t("Aufbewahrungsfrist|Löschfrist|Archiv", "Retention period|Deletion period|Archive")),
  },
  // ── Persönlich (Profilmenü oben rechts) ──
  {
    key: "profil", gruppe: "persoenlich", seite: "profil", to: PROFIL_WEG, studio: true,
    titel: () => t("Mein Profil", "My profile"),
    kurz: () => t("Dein Name, deine Anmeldung und deine Sprache.", "Your name, sign-in and language."),
    optional: true,
    begriffe: () => liste(t("Persönliche Angaben|Anzeigename|Funktion|Kürzel|Gespeicherte Unterschrift|Zeitzone", "Personal details|Display name|Job title|Initials|Saved signature|Time zone")),
    synonyme: () => liste(t("Profil|Name|Unterschrift", "Profile|Name|Signature")),
  },
  {
    key: "sicherheit", gruppe: "persoenlich", seite: "sicherheit", to: SICHERHEIT_WEG, studio: true,
    titel: () => t("Anmeldung & Sicherheit", "Sign-in & security"),
    kurz: () => t("Passwort, Zwei-Faktor und Geräte.", "Password, two-factor and devices."),
    optional: true,
    begriffe: () => liste(t("Anmeldung|Passwort|Anmeldeadresse|Zweiter Faktor|Sitzungen", "Sign-in|Password|Sign-in address|Second factor|Sessions")),
    synonyme: () => liste(t("Passwort ändern|Abmelden|Geräte", "Change password|Sign out|Devices")),
    neutral: ["2FA"],
  },
  {
    key: "benachrichtigungen", gruppe: "persoenlich", seite: null, to: "/agentur/benachrichtigungen#kanaele", studio: true,
    titel: () => t("Benachrichtigungen", "Notifications"),
    kurz: () => t("Worüber du per Mail und Glocke informiert wirst.", "What you are notified about by email and bell."),
    optional: true,
    begriffe: () => liste(t("Kanäle|Benachrichtigungen auf dem Desktop", "Channels|Desktop notifications")),
    synonyme: () => liste(t("Glocke|Hinweise|Tagesübersicht", "Bell|Notices|Daily summary")),
  },
  {
    key: "sprache", gruppe: "persoenlich", seite: "profil", anker: "sprache", to: `${PROFIL_WEG}#sprache`, studio: true,
    titel: () => t("Sprache", "Language"),
    kurz: () => t("Sprache der Oberfläche.", "Interface language."),
    optional: true,
    begriffe: () => liste(t("Sprache und Hinweise", "Language and notices")),
    synonyme: () => liste(t("Oberflächensprache|Englisch|Deutsch", "Interface language|English|German")),
  },
  {
    key: "design", gruppe: "persoenlich", seite: "profil", anker: "darstellung", to: `${PROFIL_WEG}#darstellung`, studio: true,
    titel: () => t("Design", "Design"),
    kurz: () => t("Hell, dunkel oder wie das Gerät.", "Light, dark or like the device."),
    optional: true,
    begriffe: () => liste(t("Darstellung|Hell|Dunkel|Automatisch", "Appearance|Light|Dark|Automatic")),
    synonyme: () => liste(t("Dunkelmodus|Hellmodus|Farbschema", "Dark mode|Light mode|Colour scheme")),
  },
];

export function karte(key: string): Karte | undefined {
  return KARTEN.find((k) => k.key === key);
}

// ── Rechte ──────────────────────────────────────────────────────────────────────

export interface Sicht {
  /** Admin der Agentur (chef_manager) */
  chef: boolean;
  /** Creator-Konto (Studio) */
  creator?: boolean;
}

/**
 * Welche Karten jemand in „Alle Einstellungen“ sieht. Agentur-Themen bearbeitet nur der Admin (jede Seite prüft das, die
 * Datenbank ohnehin); Manager ohne Adminrecht sehen deshalb keine Agentur-Karte (weglassen statt ausgrauen), nur ihre
 * persönlichen Punkte. Der Admin sieht in „Alle Einstellungen“ nur Agentur-Themen; Persönliches steht im Profilmenü.
 * Studio (Creator-Konto): eigene kleinere Liste ohne Team, Provision, Creator-Sicht und was es dort nicht gibt.
 */
export function sichtbareKarten(s: Sicht): Karte[] {
  if (!s.chef) return KARTEN.filter((k) => k.gruppe === "persoenlich");
  return KARTEN.filter((k) => k.gruppe !== "persoenlich" && (!s.creator || k.studio));
}

/** Karten, die die Suche durchsucht: die sichtbaren und für jeden die persönlichen (Passwort, Sprache …). */
export function suchbareKarten(s: Sicht): Karte[] {
  const sichtbar = sichtbareKarten(s);
  return [...sichtbar, ...KARTEN.filter((k) => k.gruppe === "persoenlich" && !sichtbar.includes(k))];
}

/** Gruppen mit ihren sichtbaren Karten (leere Gruppen fallen weg). */
export function kartenGruppen(s: Sicht): { gruppe: Gruppe; karten: Karte[] }[] {
  const sichtbar = sichtbareKarten(s);
  return GRUPPEN.map((g) => ({ gruppe: g, karten: sichtbar.filter((k) => k.gruppe === g.key) })).filter((g) => g.karten.length > 0);
}

/** Darf diese Person die Seite öffnen? (Sicherheitsnetz der Seiten: Wer eine Seite nicht sieht, landet auf der Startseite.) */
export function seiteSichtbar(seite: SeiteKey, s: Sicht): boolean {
  return sichtbareKarten(s).some((k) => k.seite === seite);
}

// ── Tarifrechte (I-1) ──────────────────────────────────────────────────────────

/**
 * Ist die Karte im Tarif gesperrt? Gleiche Quelle und Regel wie „Was ist in meinem Tarif an“ (org_entitlements, modulGesperrt;
 * im Test mit vollem Umfang ist alles frei). Ohne geladenen Tarifstand gilt nichts als gesperrt; die Oberfläche wartet dann.
 */
export function karteGesperrt(k: Karte, limits: Record<string, number> | null | undefined): boolean {
  return !!k.modul && !!limits && modulGesperrt(limits, k.modul);
}

/** Etikett und Upgrade-Ziel einer gesperrten Karte: „ab Team“ bzw. „ab Creator Pro“ (Creator-Konto). */
export function sperrEtikett(k: Karte, katalog: TarifKatalog | null | undefined, creator = false): { text: string; tarif: string; to: string } | null {
  if (!k.modul) return null;
  const tarif = tarifMitModul(k.modul, katalog, creator) ?? (creator ? "Creator Pro" : MODUL_AB_TARIF[k.modul] === "agentur" ? t("Agentur", "Agency") : "Team");
  const key = tarifKeyMitModul(k.modul, katalog, creator) ?? (creator ? "creator_pro" : MODUL_AB_TARIF[k.modul]);
  return { text: t(`ab ${tarif}`, `from ${tarif}`), tarif, to: wechselLink(key) };
}

// ── Suche ───────────────────────────────────────────────────────────────────────

export interface Treffer {
  karte: Karte;
  /** Gefundener Begriff, wenn es nicht der Titel ist */
  grund: string | null;
  punkte: number;
}

/** Kleinbuchstaben ohne Akzente und Trennzeichen: „Gebühr“ = „gebuhr“, „E-Mail“ = „email“. */
export function normalisiere(s: string): string {
  return s.toLowerCase().replace(/ß/g, "ss").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
}

/**
 * Sucht in Kartentiteln, Abschnittsüberschriften, Feldbeschriftungen und Synonymen. Jedes Wort der Anfrage muss vorkommen
 * (als Wortanfang oder im Wort). Reihenfolge: Titel vor Synonym vor Überschrift/Feld, Wortanfang vor „im Wort“.
 */
export function sucheEinstellungen(anfrage: string, karten: readonly Karte[], max = 8): Treffer[] {
  const woerter = normalisiere(anfrage).split(" ").filter(Boolean);
  if (!woerter.length) return [];
  const passt = (text: string): number => {
    const n = normalisiere(text);
    if (!n) return 0;
    const teile = n.split(" ");
    let summe = 0;
    for (const wort of woerter) {
      if (teile.some((x) => x === wort)) summe += 3;
      else if (teile.some((x) => x.startsWith(wort))) summe += 2;
      else if (n.replace(/ /g, "").includes(wort)) summe += 1;
      else return 0;
    }
    return summe;
  };
  const out: Treffer[] = [];
  for (const k of karten) {
    const titel = k.titel();
    // Gleich gute Titel: der kürzere gewinnt („Abo & Tarif“ vor „Was ist in meinem Tarif an“)
    const titelTreffer = passt(titel);
    let best = titelTreffer ? titelTreffer * 10 + woerter.length / Math.max(1, normalisiere(titel).split(" ").length) : 0;
    let grund: string | null = null;
    const quellen: [string[], number][] = [[k.synonyme?.() ?? [], 6], [[...(k.neutral ?? [])], 6], [k.begriffe(), 4]];
    for (const [texte, gewicht] of quellen) {
      for (const text of texte) {
        const p = passt(text) * gewicht;
        if (p > best) { best = p; grund = text; }
      }
    }
    // Persönliches und Ziele außerhalb nach den Agentur-Themen
    if (best > 0) out.push({ karte: k, grund: grund && normalisiere(grund) !== normalisiere(titel) ? grund : null, punkte: best - (k.gruppe === "persoenlich" ? 1 : 0) });
  }
  return out.sort((a, b) => b.punkte - a.punkte || KARTEN.indexOf(a.karte) - KARTEN.indexOf(b.karte)).slice(0, max);
}

// ── „⋯ → Voreinstellungen“ in den Listen (Schritt 3) ─────────────────────────

/** Listen mit einem Menü „⋯“, das zu den Einstellungen des Moduls springt (Zoho: Voreinstellungen). */
export type ListenModul = "kooperationen" | "rechnungen" | "anfragen" | "vertraege" | "creator" | "marken" | "content";
export const VOREINSTELLUNGEN: Readonly<Record<ListenModul, string>> = {
  kooperationen: w("kooperationen"),
  // Rechnungen haben mehrere Seiten (Zahlungsziele, Prüfregeln, Nummernkreise, Mahnwesen): die Gruppe auf der Startseite
  rechnungen: `${EINST}#geld`,
  anfragen: w("anfragen"),
  vertraege: w("pdf-vorlagen", "vertrag"),
  creator: w("creator-sicht"),
  marken: w("zahlungsziele"),
  content: w("marken-freigabe"),
};

// ── Vollbild und alte Wege ──────────────────────────────────────────────────────

/** Läuft dieser Weg im Vollbild „Alle Einstellungen“ (ohne Arbeitsnavigation)? Persönliches bleibt im Arbeitsrahmen. */
export function istVollbild(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, "");
  if (p === EINST) return true;
  if (!p.startsWith(`${EINST}/`)) return false;
  const rest = p.slice(EINST.length + 1);
  return rest !== "sicherheit" && rest !== "profil";
}

/**
 * Alte Anker der Seite „Konditionen“ (Stand vor IA-1) → neue Seite. Ohne bekannten Anker geht es zu „Alle Einstellungen“,
 * Gruppe Geld & Abrechnung.
 */
export const KONDITIONEN_ANKER: Readonly<Record<string, SeiteKey>> = {
  "creator-sicht": "creator-sicht",
  "herkunft-nachlauf": "herkunft-nachprovision",
  "koop-einstellungen": "kooperationen",
  "koop-staffeln": "staffel",
  "marke-einstellungen": "marken-freigabe",
  "angebote-einstellungen": "anfragen",
  "nummernkreis-title": "nummernkreise",
  "provision": "provision",
  "zahlungsziele": "zahlungsziele",
  "pruefregeln": "pruefregeln",
  "wiedervorlagen": "wiedervorlagen",
};
export function konditionenZiel(hash: string): string {
  const anker = decodeURIComponent(hash.replace(/^#/, ""));
  const seite = KONDITIONEN_ANKER[anker];
  return seite ? `${EINST}/${seite}` : `${EINST}#geld`;
}

/** Abo & Tarif: „Verträge und Rechtstexte“ hat eine eigene Seite; Mails verweisen weiter auf abo#vertraege. */
export const ABO_ANKER_WEITER: Readonly<Record<string, string>> = { vertraege: `${EINST}/recht` };

/**
 * 0541 (Praxistest 04.11.2026): Im Tarif Enterprise gibt es weder Kündigungsknopf noch Zahlungsart; die Einträge „Kündigung &
 * Daten“ und „Zahlungsart & Rechnungen“ springen dort auf die Abschnitte der Enterprise-Ansicht.
 */
export const ENTERPRISE_ANKER: Readonly<Record<string, string>> = { "kuendigung-title": "enterprise-kuendigung", zahlungsart: "enterprise-titel" };
export function aboAnker(hash: string, enterprise: boolean): string {
  const id = decodeURIComponent(hash.replace(/^#/, ""));
  return enterprise ? ENTERPRISE_ANKER[id] ?? id : id;
}

/** Unbekannte Unterseite der Einstellungen: zur Startseite. */
export const UNBEKANNT_ZIEL = EINST;

/** Welche Seite gehört zur Karte (für den Baum), und ist sie die aktive? */
export function karteAktiv(k: Karte, seite: string, hash: string): boolean {
  if (k.seite !== seite) return false;
  const h = hash.replace(/^#/, "");
  if (k.anker) return h === k.anker;
  // Ohne Anker aktiv, solange kein anderer Karten-Anker dieser Seite gewählt ist
  return !KARTEN.some((x) => x.seite === seite && x.anker && x.anker === h);
}

// ── Letzte Arbeitsseite (für „Einstellungen schließen“) ─────────────────────────

const MERK = "tk-einst-zurueck";
let letzteArbeitsseite: string | null = null;
/** Merkt die zuletzt besuchte Arbeitsseite (außerhalb des Vollbilds), solange die Sitzung läuft. */
export function merkeArbeitsseite(weg: string): void {
  if (istVollbild(weg.split(/[?#]/)[0] ?? "")) return;
  letzteArbeitsseite = weg;
  try { sessionStorage.setItem(MERK, weg); } catch { /* ohne Speicher reicht die Variable */ }
}
export function arbeitsseite(): string {
  if (letzteArbeitsseite) return letzteArbeitsseite;
  try { return sessionStorage.getItem(MERK) || "/agentur"; } catch { return "/agentur"; }
}
